import { ImageMetricStatus } from './image-metrics.utils';

//Категории метрик производительности
export enum PerformanceMetricCategory {
  /** Метрики запуска приложения */
  APP_START = 'app_start',
  /** Метрики отрисовки экранов */
  SCREEN_RENDER = 'screen_render',
  /** Метрики отрисовки компонентов/вью */
  VIEW_RENDER = 'view_render',
  /** Сетевые метрики */
  NETWORK = 'network',
  /** Загрузка медиа (изображения и т.п.) */
  IMAGE_LOAD = 'image.load',
  /** Web Vitals метрики */
  WEB_VITALS = 'web_vitals',
}

//События для запуска приложения
export enum AppStartEvent {
  COLD_START = 'cold_start',
  WARM_START = 'warm_start',
}

//Названия экранов для метрик
export enum ScreenName {
  HOME = 'home',
  CATALOG_LISTING = 'catalog_listing',
  PRODUCT = 'product_page',
  CART = 'cart',
  CHECKOUT = 'checkout',
  ACTION = 'action',
}

//Базовые обязательные атрибуты для всех метрик
export interface PerformanceMetricBaseAttributes {
  /** Время сбора метрики */
  timestamp: number;
  /** Идентификатор устройства */
  device_id: string;
  /** Платформа (iOS/Android/Web) */
  platform: string;
  /** Версия приложения */
  version: string;
  /** Продолжительность выполнения в миллисекундах */
  duration: number;
}

//Атрибуты для метрик запуска приложения
export interface AppStartMetricAttributes extends PerformanceMetricBaseAttributes {
  /** Тип старта приложения */
  start_type: AppStartEvent;
}

//Атрибуты для метрик отрисовки экранов
export interface ScreenRenderMetricAttributes extends PerformanceMetricBaseAttributes {
  /** Название экрана */
  screen_name: ScreenName | string;
  /** ID трейса для связи с другими метриками */
  trace_id?: string;
  /** URL блокирующих запросов */
  blocking_requests?: string[];
}

//Атрибуты для метрик отрисовки компонентов
export interface ViewRenderMetricAttributes extends PerformanceMetricBaseAttributes {
  /** Название компонента/вью */
  view_name: string;
  /** ID родительского экрана */
  parent_screen?: ScreenName | string;
  /** ID трейса для связи с другими метриками */
  trace_id?: string;
}

//Атрибуты для сетевых метрик
export interface NetworkMetricAttributes extends PerformanceMetricBaseAttributes {
  /** URL запроса */
  request_url: string;
  /** HTTP метод */
  http_method: string;
  /** Код ответа */
  response_code: number;
  /** Размер запроса в байтах */
  request_size?: number;
  /** Размер ответа в байтах */
  response_size?: number;
  /** ID трейса */
  trace_id?: string;
}

//Универсальный тип атрибутов метрик
export interface ImageLoadMetricAttributes extends PerformanceMetricBaseAttributes {
  'image.component_name': string;
  'image.requested_src': string;
  'image.rendered_src'?: string;
  'image.width'?: number;
  'image.height'?: number;
  'image.priority'?: string;
  'image.image_category'?: string;
  'image.load_status': string;
  'image.natural_width'?: number;
  'image.natural_height'?: number;
  'image.displayed_width'?: number;
  'image.displayed_height'?: number;
  'image.current_src'?: string;
  'image.region_slug'?: string;
  'image.region_name'?: string;
  'image.error_message'?: string;
  'image.qa_id'?: string;
  'image.alt'?: string;
  'image.screen'?: string;
  'image.type'?: string;
  'image.duration_ms'?: number;
}

export interface ImageLoadMetricPayload {
  componentName: string;
  requestedSrc: string;
  renderedSrc?: string;
  width?: number;
  height?: number;
  priority?: string;
  imageCategory?: string;
  status: ImageMetricStatus;
  duration: number;
  naturalWidth?: number;
  naturalHeight?: number;
  displayedWidth?: number;
  displayedHeight?: number;
  currentSrc?: string;
  regionSlug?: string;
  regionName?: string;
  errorMessage?: string;
  qaId?: string;
  alt?: string;
  screen?: ScreenName | string;
}

export interface ImageLoadStartPayload {
  componentName: string;
  requestedSrc: string;
  renderedSrc?: string;
  width?: number;
  height?: number;
  priority?: string;
  imageCategory?: string;
  qaId?: string;
  alt?: string;
  screen?: ScreenName | string;
  regionSlug?: string;
}

export interface ImageLoadCompletePayload {
  status: ImageMetricStatus;
  duration: number;
  naturalWidth?: number;
  naturalHeight?: number;
  displayedWidth?: number;
  displayedHeight?: number;
  currentSrc?: string;
  regionName?: string;
  errorMessage?: string;
}

export interface NetworkRequestStartPayload {
  requestUrl: string;
  httpMethod: string;
  requestSize?: number;
  startTime?: number;
}

export interface NetworkRequestCompletePayload {
  responseCode: number;
  responseSize?: number;
  duration: number;
  traceId?: string;
  endTime?: number;
}

//Атрибуты для Web Vitals метрик
export interface WebVitalsMetricAttributes extends PerformanceMetricBaseAttributes {
  /** URL страницы */
  page_url: string;
  /** User Agent */
  user_device: string;
  /** Web Vitals event ID (всегда присутствует) */
  'web_vitals.event_id': string;
  /** Web Vitals trace ID (всегда присутствует) */
  'web_vitals.trace_id': string;
  /** Web Vitals release version (всегда присутствует) */
  'web_vitals.release': string;
  /** Sentry event ID (только если данные из Sentry) */
  'sentry.event_id'?: string;
  /** Sentry trace ID (только если данные из Sentry) */
  'sentry.trace_id'?: string;
  /** Sentry release version (только если данные из Sentry) */
  'sentry.release'?: string;
  /** Cumulative Layout Shift (сырое значение) */
  'webvital.cls.raw': number;
  /** Cumulative Layout Shift (рейтинг) */
  'webvital.cls.rating': string;
  /** First Contentful Paint (сырое значение) */
  'webvital.fcp.raw': number;
  /** First Contentful Paint (рейтинг) */
  'webvital.fcp.rating': string;
  /** Largest Contentful Paint (сырое значение) */
  'webvital.lcp.raw': number;
  /** Largest Contentful Paint (рейтинг) */
  'webvital.lcp.rating': string;
  /** Interaction to Next Paint (сырое значение) */
  'webvital.inp.raw'?: number;
  /** Interaction to Next Paint (рейтинг) */
  'webvital.inp.rating'?: string;
  /** Time to First Byte (сырое значение) */
  'webvital.ttfb.raw': number;
  /** Time to First Byte (рейтинг) */
  'webvital.ttfb.rating': string;
}

export type PerformanceMetricAttributes =
  | AppStartMetricAttributes
  | ScreenRenderMetricAttributes
  | ViewRenderMetricAttributes
  | NetworkMetricAttributes
  | ImageLoadMetricAttributes
  | WebVitalsMetricAttributes;

//Структура метрики производительности
export interface PerformanceMetric {
  /** Категория метрики */
  category: PerformanceMetricCategory;
  /** Название события */
  event: string;
  /** Атрибуты метрики */
  attributes: PerformanceMetricAttributes;
}

//Конфигурация для отслеживания экранов
export interface ScreenTrackingConfig {
  /** Название экрана */
  screenName: ScreenName | string;
  /** URL блокирующих запросов, которые должны завершиться до готовности экрана */
  blockingRequests: string[];
}

//Состояние отслеживания экрана
export interface ScreenTrackingState {
  /** Время начала загрузки экрана */
  startTime: number;
  /** Сопоставленные блокирующие запросы (pattern -> фактический URL) */
  matchedBlockingRequests: Map<string, string>;
  /** Флаг готовности экрана */
  isReady: boolean;
  /** Текущее traceId экрана */
  traceId?: string;
}
